import {
  createContext,
  useState,
  Dispatch,
  SetStateAction,
  ReactNode,
  useContext,
  useEffect,
} from 'react'
import { SanityBlogSettings } from '@data/sanity/queries/types/blog'
import { LanguageContext, Locale } from './language'
import { getPageUrl, PageType } from './routes'

interface Metadata {
  metaTitle?: string
  metaDescription?: string
  shareTitle?: string
  shareDescription?: string
  shareGraphicUrl?: string
}

interface MetadataContextProps {
  metadata: Metadata
  setMetadata: Dispatch<SetStateAction<Metadata>>
  metadataOverride: Metadata
  setMetadataOverride: Dispatch<SetStateAction<Metadata>>
}

export const MetadataContext = createContext<MetadataContextProps>({
  metadata: {},
  setMetadata: () => {},
  metadataOverride: {},
  setMetadataOverride: () => {},
})

interface MetadataContextProviderProps {
  children: ReactNode
}

export const MetadataContextProvider = ({
  children,
}: MetadataContextProviderProps) => {
  const [metadata, setMetadata] = useState<Metadata>({})
  const [metadataOverride, setMetadataOverride] = useState<Metadata>({})

  return (
    <MetadataContext.Provider
      value={{
        metadata,
        setMetadata,
        metadataOverride,
        setMetadataOverride,
      }}
    >
      {children}
    </MetadataContext.Provider>
  )
}

/**
 * Blog canonical URL hook.
 */
export const useBlogCanonicalUrl = (
  pageType: PageType,
  slug: string,
  blogSettings: SanityBlogSettings | null,
) => {
  const { locale } = useContext(LanguageContext)

  const [canonicalUrl, setCanonicalUrl] = useState<string>()

  useEffect(() => {
    // Check if blog inheritance is set
    if (
      typeof window === 'undefined' ||
      !blogSettings?.inheritContent ||
      !blogSettings?.sourceLocale
    ) {
      return
    }

    const origin = window.location.origin
    const pageUrl = getPageUrl(pageType, slug, {
      locale,
      newLocale: blogSettings.sourceLocale as Locale,
    })

    setCanonicalUrl(`${origin}${pageUrl}`)
  }, [locale, pageType, slug, blogSettings])

  return canonicalUrl
}
